import { addUtcMonths, firstPeriodEndAtOrAfter } from "@intelligems/ig-types";

import { entityPriceIdsOnSubscription, subscriptionForEntity } from "./catalog";
import type {
  BillingCatalog,
  BillingModule,
  BillingPlan,
  BillingSnapshot,
  RawSubscriptionAccess,
} from "./types";

type Subscription = RawSubscriptionAccess["subscriptions"]["active"][number];

export type BillingPeriod = Pick<
  Subscription,
  "billingCycleAnchor" | "interval" | "intervalCount" | "periodEnd"
>;

type CommitmentTerms = Pick<
  BillingPlan | BillingModule,
  "commitment" | "startDate"
>;

const DAY_SECONDS = 86_400;

/**
 * When a commitment of `months` that started at `startSec` runs out: that many
 * calendar months after the start's month, on the billing-cycle anchor's day
 * and time of day, so a commitment that started on a boundary ends on one.
 * Day and week periods fall on no day of the month, so there the months run
 * from the start itself and the end is the first period end at or after them.
 */
export const commitmentEndAt = (
  startSec: number,
  months: number,
  { billingCycleAnchor, interval, intervalCount, periodEnd }: BillingPeriod
): number => {
  const start = new Date(startSec * 1_000);
  if (interval === "day" || interval === "week") {
    const monthsOn =
      addUtcMonths(start, months, start.getUTCDate()).getTime() / 1_000;
    const periodSeconds =
      (interval === "day" ? 1 : 7) * intervalCount * DAY_SECONDS;
    return (
      periodEnd +
      Math.ceil((monthsOn - periodEnd) / periodSeconds) * periodSeconds
    );
  }

  const anchor = new Date((billingCycleAnchor ?? periodEnd) * 1_000);
  const anchorTimeInStartMonth = new Date(
    Date.UTC(
      start.getUTCFullYear(),
      start.getUTCMonth(),
      1,
      anchor.getUTCHours(),
      anchor.getUTCMinutes(),
      anchor.getUTCSeconds()
    )
  );
  return (
    addUtcMonths(
      anchorTimeInStartMonth,
      months,
      anchor.getUTCDate()
    ).getTime() / 1_000
  );
};

export const commitmentHasEnded = (
  { commitment, startDate }: CommitmentTerms,
  period: BillingPeriod,
  nowSec: number
): boolean =>
  commitment <= 0 ||
  startDate === undefined ||
  commitmentEndAt(startDate, commitment, period) <= nowSec;

/**
 * The whole months of the billing calendar completed between `startSec` and
 * `nowSec`, counted the way a commitment is.
 */
export const billingMonthsSince = (
  startSec: number,
  period: BillingPeriod,
  nowSec: number
): number => {
  const start = new Date(startSec * 1_000);
  const now = new Date(nowSec * 1_000);
  let months =
    (now.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    now.getUTCMonth() -
    start.getUTCMonth();
  while (months > 0 && commitmentEndAt(startSec, months, period) > nowSec) {
    months -= 1;
  }
  return Math.max(months, 0);
};

/**
 * The latest end among the minimum commitments still running at `nowSec`, or
 * null when none is.
 */
export const latestCommitmentEnd = (
  entities: readonly CommitmentTerms[],
  period: BillingPeriod,
  nowSec: number
): number | null =>
  entities.reduce<number | null>((latest, { commitment, startDate }) => {
    if (commitment <= 0 || startDate === undefined) {
      return latest;
    }
    const end = commitmentEndAt(startDate, commitment, period);
    return end > nowSec && (latest === null || end > latest) ? end : latest;
  }, null);

export type PauseBoundary = Readonly<{
  // The boundary the pause lands on.
  boundary: number;
  // What the pause request asks for: the end of the commitment that holds the
  // subscription, or null to take its current period end.
  requestedAt: number | null;
  subscription: Subscription;
}>;

/**
 * Where a pause of `entityId` parks: the subscription it is billed on, held
 * until every commitment on that subscription has run out.
 */
export const pauseBoundaryFor = ({
  catalog,
  entityId,
  nowSec,
  snapshot,
}: {
  catalog: BillingCatalog;
  entityId: string;
  nowSec: number;
  snapshot: BillingSnapshot;
}): PauseBoundary | undefined => {
  const subscription = subscriptionForEntity({ catalog, entityId, snapshot });
  if (!subscription) {
    return undefined;
  }

  const entitiesOnSubscription = [
    ...(catalog.currentPlan ? [catalog.currentPlan] : []),
    ...catalog.activeModules,
  ].filter(
    ({ id }) =>
      entityPriceIdsOnSubscription({
        catalog,
        entityId: id,
        snapshot,
        subscription,
      }).length > 0
  );
  const requestedAt = latestCommitmentEnd(
    entitiesOnSubscription,
    subscription,
    nowSec
  );

  const boundary = firstPeriodEndAtOrAfter(subscription, requestedAt);
  if (boundary === undefined) {
    return undefined;
  }

  return { boundary, requestedAt, subscription };
};

/**
 * Where a scheduled cancellation of `entity` lands: the first period end at or
 * after its commitment end on the subscription the cancel targets, the
 * boundary the backend snaps the request to. Undefined wherever the backend
 * refuses one: the commitment start or the subscription is unknown, the
 * subscription's current period end has already passed, or the boundary cannot
 * be placed on its billing calendar.
 */
export const cancellationBoundaryFor = ({
  catalog,
  commitmentStartAt,
  entity,
  nowSec,
  snapshot,
}: {
  catalog: BillingCatalog;
  commitmentStartAt?: number;
  entity: CommitmentTerms & Pick<BillingPlan | BillingModule, "id">;
  nowSec: number;
  snapshot: BillingSnapshot;
}): { boundary: number; commitmentEnd: number } | undefined => {
  const startAt = commitmentStartAt ?? entity.startDate;
  const subscription = subscriptionForEntity({
    catalog,
    entityId: entity.id,
    snapshot,
  });
  if (
    startAt === undefined ||
    !subscription ||
    subscription.periodEnd <= nowSec
  ) {
    return undefined;
  }

  const commitmentEnd = commitmentEndAt(
    startAt,
    entity.commitment,
    subscription
  );
  const boundary = firstPeriodEndAtOrAfter(subscription, commitmentEnd);
  return boundary === undefined ? undefined : { boundary, commitmentEnd };
};
